import {ChangeEvent, FC, InputHTMLAttributes} from 'react';
import clsx from 'clsx';
import Button, {SIZES} from 'components/Button';
import TooltipWrapper from 'components/Tooltip';
import DropdownContext from '../context';
import Drawer from '../Drawer';
import useDropdown from '../hooks/useDropdown';
import {BaseDropdown, FieldType, OptionsLocal, SingleChoice} from '../type';
import {field} from './styles.module.css';

export type DropdownInputProps = {
    hasClearSelection?: boolean;
    isSearchable?: boolean;
    maxLength?: number;
    placeholder?: string;
    readonly?: boolean;
    type: Extract<FieldType, 'input'>;
} & SingleChoice &
    OptionsLocal &
    BaseDropdown &
    InputHTMLAttributes<HTMLInputElement>;

const DropdownInput: FC<DropdownInputProps> = ({
    disabled,
    drawerClassName,
    error,
    fieldClassName,
    hasClearSelection,
    isCompact,
    isSearchable,
    kind = 'secondary',
    maxLength,
    message,
    onChange,
    options = [],
    placeholder,
    readonly,
    type,
    value,
    ...rest
}) => {
    const providerValue = useDropdown({
        disabled,
        hasClearSelection,
        isSearchable,
        onChange,
        options,
        readonly,
        type,
        value,
    });
    const {isShown, onClickField, refField, setTooltipRef} = providerValue;

    const displayValue = () => {
        if (value !== undefined) {
            return (
                (options.find((option) => option.value === value)
                    ?.label as string) || value
            );
        }

        return '';
    };

    const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) =>
        onChange &&
        onChange(
            options.find((option) => option.label === event.target.value)
                ?.value || event.target.value,
        );

    return (
        <DropdownContext.Provider value={providerValue}>
            <div
                ref={disabled || readonly ? (ref) => setTooltipRef(ref) : null}
                className={clsx((disabled || readonly) && 'cursor-not-allowed')}
                {...rest}
            >
                <div
                    ref={refField}
                    aria-controls="dropdown-select"
                    aria-expanded={isShown}
                    aria-label="input"
                    className={clsx(
                        field,
                        'input-wrapper',
                        isCompact ? 'h-8' : 'h-10',
                        fieldClassName,
                    )}
                    data-compact={isCompact}
                    data-disabled={disabled || readonly}
                    data-dropdown={true}
                    data-invalid={!!error}
                    data-kind={kind}
                    data-type="input"
                    role="combobox"
                    tabIndex={-1}
                >
                    <input
                        ref={
                            disabled || readonly
                                ? null
                                : (ref) => setTooltipRef(ref)
                        }
                        className={clsx(
                            'flex-1 min-w-0 rounded-none border-none',
                            SIZES[isCompact ? 'sm' : 'base'],
                        )}
                        disabled={disabled || readonly}
                        maxLength={maxLength}
                        onChange={handleChangeInput}
                        placeholder={placeholder}
                        readOnly={readonly}
                        tabIndex={readonly || disabled ? -1 : undefined}
                        type="text"
                        value={displayValue()}
                    />
                    {kind !== 'tertiary' && (
                        <div
                            className={clsx(
                                disabled || readonly
                                    ? 'border-grey-300'
                                    : 'border-r-grey-400',
                                isCompact ? 'h-8' : 'h-10',
                                'border-r',
                            )}
                        />
                    )}
                    <TooltipWrapper message={message}>
                        <Button
                            className="rounded-none"
                            disabled={
                                disabled || readonly || options.length === 0
                            }
                            icon={
                                isShown ? 'small_arrow_up' : 'small_arrow_down'
                            }
                            kind={kind}
                            onClick={onClickField}
                            size={isCompact ? 'sm' : 'base'}
                            tabIndex={-1}
                        />
                    </TooltipWrapper>
                </div>
                {isShown && refField.current && (
                    <Drawer className={drawerClassName} />
                )}
            </div>
        </DropdownContext.Provider>
    );
};

export default DropdownInput;
